import { useCallback } from 'react'
import { isProduction } from 'apis/formatUrl'
import { useCareCompleteBooking } from './useAppointment'
import { usePersonalInfo } from 'contexts/AuthContext'
import { useApplyTags } from './careHooks'

const tagMapStaging = {
  FL: [
    { id: '15319', name: 'Digital-MSK' },
    { id: '12787', name: 'Digital-WLC' },
    { id: '27494', name: 'Digital-PT' },
    { id: '2075', name: 'HMO' },
    { id: '3400', name: 'New Patient' },
    { id: '2076', name: 'PPO' },
    { id: '3825', name: 'RD-NB' },
    { id: '30650', name: 'English' },
    { id: '30651', name: 'Spanish' },
    { id: '65969', name: 'Access' }, //added in BE
  ],
  NY: [
    { id: '15320', name: 'Digital-MSK' },
    { id: '12789', name: 'Digital-WLC' },
    { id: '27496', name: 'Digital-PT' },
    { id: '2073', name: 'HMO' },
    { id: '3401', name: 'New Patient' },
    { id: '2074', name: 'PPO' },
    { id: '3826', name: 'RD-NB' },
    { id: '30648', name: 'English' },
    { id: '30649', name: 'Spanish' },
    { id: '65970', name: 'Access' }, //added in BE
  ],
  CA: [
    { id: '15318', name: 'Digital-MSK' },
    { id: '12788', name: 'Digital-WLC' },
    { id: '27495', name: 'Digital-PT' },
    { id: '2071', name: 'HMO' },
    { id: '3399', name: 'New Patient' },
    { id: '2072', name: 'PPO' },
    { id: '3824', name: 'RD-NB' },
    { id: '30646', name: 'English' },
    { id: '30647', name: 'Spanish' },
    { id: '65967', name: 'Access' }, //added in BE
  ],
}

const tagMapProd = {
  FL: [
    { id: '51647', name: 'Digital-MSK' },
    { id: '37485', name: 'Digital-WLC' },
    { id: '63852', name: 'Digital-PT' },
    { id: '27183', name: 'HMO' },
    { id: '34024', name: 'New Patient' },
    { id: '27184', name: 'PPO' },
    { id: '37484', name: 'RD-NB' },
    { id: '66153', name: 'Spanish' },
    { id: '', name: 'Access' }, //added in BE
  ],
  NY: [
    { id: '51645', name: 'Digital-MSK' },
    { id: '37488', name: 'Digital-WLC' },
    { id: '63854', name: 'Digital-PT' },
    { id: '27181', name: 'HMO' },
    { id: '34026', name: 'New Patient' },
    { id: '27182', name: 'PPO' },
    { id: '37487', name: 'RD-NB' },
    { id: '66155', name: 'Spanish' },
    { id: '', name: 'Access' }, //added in BE
  ],
  CA: [
    { id: '51526', name: 'Digital-MSK' },
    { id: '37491', name: 'Digital-WLC' },
    { id: '63853', name: 'Digital-PT' },
    { id: '27179', name: 'HMO' },
    { id: '34025', name: 'New Patient' },
    { id: '27180', name: 'PPO' },
    { id: '37490', name: 'RD-NB' },
    { id: '66154', name: 'Spanish' },
    { id: '', name: 'Access' }, //added in BE
  ],
}

export const useApplyNewPatientTag = () => {
  const { personalInfo, applyHealthieTags } = useCareCompleteBooking()

  const applyNewPatientTag = useCallback(() => {
    const { state } = personalInfo || {}
    if (state?.code === 'CA') {
      applyHealthieTags({
        tagId: isProduction() ? '34025' : '3399',
      })
    } else if (state?.code === 'NY') {
      applyHealthieTags({
        tagId: isProduction() ? '34026' : '3401',
      })
    } else {
      applyHealthieTags({
        tagId: isProduction() ? '34024' : '3400',
      })
    }
  }, [applyHealthieTags, personalInfo])

  return { applyNewPatientTag }
}

export const getTagIdMap = () => {
  return isProduction() ? tagMapProd : tagMapStaging
}

export const getTagId = (stateCode, name) => {
  const tagIdMap = getTagIdMap()
  const tagList = tagIdMap[stateCode || 'FL'] || tagIdMap.FL
  return tagList.find((item) => item.name === name)?.id
}

export const useApplyLanguageTag = () => {
  const { personalInfo, applyHealthieTags } = useCareCompleteBooking()

  const getLanguageTagId = useCallback(
    (languageCode) => {
      const { state } = personalInfo || {}
      switch (languageCode) {
        case 'es':
          return getTagId(state.code, 'Spanish')
        default:
          return getTagId(state.code, 'English')
      }
    },
    [personalInfo]
  )

  const applyLanguageTag = useCallback(
    (languageCode) => {
      const tagId = getLanguageTagId(languageCode)
      if (tagId) {
        applyHealthieTags({
          tagId,
        })
      }
    },
    [applyHealthieTags, getLanguageTagId]
  )

  return { applyLanguageTag }
}

export const useApplyHealthieTag = () => {
  const { personalInfo } = usePersonalInfo()
  const { applyHealthieTags } = useApplyTags()

  const applyTags = useCallback(
    (tagName) => {
      const { state } = personalInfo || {}
      const tagId = getTagId(state?.code, tagName)
      if (tagId) {
        applyHealthieTags({
          tagId,
        })
      }
    },
    [applyHealthieTags, personalInfo]
  )

  return { applyTags }
}
